import { useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, ic } from "../wizard-icons";
import type { ShippingRow, ShippingState } from "../wizard-types";
import { ShippingForm, type Governorate } from "./InputSteps";

const K = keys.quickSetup;

const DEFAULT_PRICE = "50";
const DEFAULT_DAYS = "3";

/**
 * Delivery step (v2): defaults to a one-tap "ship everywhere in Egypt" preset
 * (a single price + delivery time applied to every active governorate);
 * "Set prices per governorate" is the secondary, opt-in path that reveals the
 * existing per-governorate `ShippingForm`. Emits a `ShippingState` union — the
 * shell expands `nationwide` into per-governorate rows at submit time so the
 * persist contract (`buildShippingRules` grouping by price|days) is untouched.
 */
export function DeliveryStep({
  governorates,
  initial,
  onDone,
  narrow,
}: {
  governorates: Governorate[];
  initial: ShippingState;
  onDone: (state: ShippingState) => void;
  narrow: boolean;
}): ReactElement {
  const t = useTranslations();
  const dir = useLocale() === "ar" ? "rtl" : "ltr";
  const [mode, setMode] = useState<"nationwide" | "custom">(initial.mode);
  const [price, setPrice] = useState(initial.mode === "nationwide" ? initial.price : DEFAULT_PRICE);
  const [days, setDays] = useState(initial.mode === "nationwide" ? initial.days : DEFAULT_DAYS);
  const [customRows] = useState<ShippingRow[]>(initial.mode === "custom" ? initial.rows : []);
  const [error, setError] = useState<string | null>(null);

  const inputBox: CSSProperties = {
    display: "flex",
    alignItems: "center",
    border: "1.5px solid var(--gold-input)",
    borderRadius: 10,
    padding: "0 14px",
    height: 48,
    background: "#fff",
    width: 140,
    boxSizing: "border-box",
  };
  const inputEl: CSSProperties = {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    fontSize: 16,
    fontWeight: 700,
    fontFamily: "inherit",
    color: "var(--navy)",
    background: "transparent",
  };
  const label: CSSProperties = { fontSize: 12, fontWeight: 700, color: "var(--muted)" };

  const handleFinish = () => {
    const p = parseInt(price, 10);
    const d = parseInt(days, 10);
    if (!p || p <= 0 || !d || d <= 0) {
      setError(t(K.shipping.needPrice));
      return;
    }
    setError(null);
    onDone({ mode: "nationwide", price: String(p), days: String(d) });
  };

  if (mode === "custom") {
    return (
      <div style={{ animation: "qs-in .3s ease", display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Returns to the one-rate preset (NOT the previous step) — labelled
            distinctly so it never reads as a duplicate of the header's Back. */}
        <button
          onClick={() => setMode("nationwide")}
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "transparent",
            border: "none",
            padding: 0,
            fontSize: 13,
            fontWeight: 700,
            color: "var(--gold-ink)",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <span style={{ display: "flex", transform: dir === "rtl" ? "scaleX(-1)" : undefined }}>
            {ic(Icons.back, 14)}
          </span>
          {t(K.shipping.backToPreset)}
        </button>
        <ShippingForm
          governorates={governorates}
          initial={customRows}
          narrow={narrow}
          onDone={(rows) => onDone({ mode: "custom", rows })}
        />
      </div>
    );
  }

  return (
    <div style={{ animation: "qs-in .3s ease", display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          border: "2px solid var(--accent)",
          borderRadius: 14,
          padding: 22,
          background: "var(--sel-bg)",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 999,
              background: "var(--accent)",
              color: "var(--navy)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {ic(Icons.check, 12, { stroke: 3 })}
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
              {t(K.shipping.nationwideTitle)}
            </span>
            <span style={{ fontSize: 13, color: "var(--muted)" }}>
              {t(K.shipping.nationwideDesc, { count: governorates.length })}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, paddingInlineStart: 38, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={label}>{t(K.shipping.priceLabel)}</label>
            <div style={inputBox}>
              <input
                value={price}
                onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))}
                inputMode="numeric"
                style={inputEl}
              />
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={label}>{t(K.shipping.daysLabel)}</label>
            <div style={inputBox}>
              <input
                value={days}
                onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ""))}
                inputMode="numeric"
                style={inputEl}
              />
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => setMode("custom")}
        style={{
          border: "1.5px solid var(--border-strong)",
          borderRadius: 14,
          padding: "20px 22px",
          background: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 14,
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "start",
          width: "100%",
        }}
      >
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: 999,
            border: "2px solid var(--dashed)",
            flexShrink: 0,
            boxSizing: "border-box",
          }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
            {t(K.shipping.customTitle)}
          </span>
          <span style={{ fontSize: 13, color: "var(--muted)" }}>{t(K.shipping.customDesc)}</span>
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--gold-ink)", whiteSpace: "nowrap" }}>
          {t(K.shipping.customize)}
        </span>
      </button>

      {error && (
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--danger)" }}>{error}</div>
      )}

      <button
        onClick={handleFinish}
        style={{
          width: "100%",
          padding: "13px 16px",
          borderRadius: 10,
          border: "none",
          background: "var(--accent)",
          color: "var(--navy)",
          fontSize: 16,
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {t(K.shipping.finish)}
      </button>
    </div>
  );
}
